import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

const RELOAD_KEY = "mediflow_chunk_reload";

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

function isChunkError(error: Error | null): boolean {
    if (!error?.message) return false;
    const msg = error.message.toLowerCase();
    return (
        msg.includes("dynamically imported module") ||
        msg.includes("loading chunk") ||
        msg.includes("loading css chunk") ||
        msg.includes("chunkloaderror") ||
        (msg.includes("failed to fetch") && error.name === "TypeError")
    );
}

export class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        // Keine PHI in Logs — nur technische Fehlerinfo
        console.error("[ErrorBoundary]", error.message, info.componentStack);

        // Bei ChunkLoadError: einmaliger kontrollierter Reload
        if (isChunkError(error)) {
            const alreadyReloaded = sessionStorage.getItem(RELOAD_KEY);
            if (!alreadyReloaded) {
                console.warn("[MediFlow] ChunkLoadError in ErrorBoundary — einmaliger Reload…");
                sessionStorage.setItem(RELOAD_KEY, Date.now().toString());
                window.location.reload();
                return;
            }
            // Falls bereits reloaded: Fehler anzeigen (kein Endlos-Loop)
            console.error("[MediFlow] ChunkLoadError nach Reload — manuelles Neuladen nötig.");
        }
    }

    /**
     * Kontrollierter Reset. Bei Chunk-Fehlern: Reload mit Loop-Schutz.
     * Bei anderen Fehlern: React State Reset.
     */
    private handleReset = () => {
        if (isChunkError(this.state.error)) {
            // Reload-Flag zurücksetzen damit der manuelle Klick funktioniert
            sessionStorage.removeItem(RELOAD_KEY);
            window.location.reload();
        } else {
            this.setState({ hasError: false, error: null });
        }
    };

    render() {
        if (this.state.hasError) {
            if (this.props.fallback) return this.props.fallback;

            return (
                <div className="flex min-h-[400px] flex-col items-center justify-center gap-4 p-8">
                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
                        <AlertTriangle className="h-7 w-7 text-red-500" />
                    </div>
                    <div className="text-center">
                        <h2 className="text-lg font-semibold text-neutral-900">
                            Etwas ist schiefgelaufen
                        </h2>
                        <p className="mt-1 max-w-md text-sm text-neutral-500">
                            Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut
                            oder kontaktieren Sie den Support.
                        </p>
                        {this.state.error && (
                            <details className="mt-3 text-left">
                                <summary className="cursor-pointer text-xs text-neutral-400 hover:text-neutral-600">
                                    Technische Details
                                </summary>
                                <pre className="mt-2 max-w-lg overflow-auto rounded-lg bg-neutral-50 p-3 text-xs text-neutral-600">
                                    {this.state.error.message}
                                </pre>
                            </details>
                        )}
                    </div>
                    <button
                        onClick={this.handleReset}
                        className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
                        aria-label="Erneut versuchen"
                    >
                        <RefreshCw className="h-4 w-4" />
                        Erneut versuchen
                    </button>
                </div>
            );
        }

        return this.props.children;
    }
}
